/*
  dr-product.js
  Variant selection, gallery and quantity for the product section.

  Variant data is embedded in the page as JSON, so changing an option updates
  price, availability and the hidden variant id with no network request. The
  URL is kept in step via history.replaceState so a refresh or a share keeps
  the chosen variant.
*/
(function () {
  // The shop's money format arrives from Liquid on the section element;
  // window.Shopify.money_format is not reliably present on the storefront.
  function money(cents, format) {
    const amount = (cents / 100).toFixed(2);
    if (!format) return amount;
    return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  // A shop-defined format may wrap the figure in markup, e.g. <span class=money>.
  function setPrice(el, text) {
    if (!el) return;
    if (text.indexOf('<') !== -1) {
      el.innerHTML = text;
    } else {
      el.textContent = text;
    }
  }

  class DrProduct {
    constructor(root) {
      this.root = root;
      this.moneyFormat = root.dataset.moneyFormat || '';
      this.variants = this.readVariants();
      if (!this.variants.length) return;

      this.priceCurrent = root.querySelector('[data-dr-price-current]');
      this.priceWas = root.querySelector('[data-dr-price-was]');
      this.priceSave = root.querySelector('[data-dr-price-save]');
      this.variantInput = root.querySelector('[data-dr-variant-id]');
      this.addButton = root.querySelector('[data-dr-add]');
      this.addText = root.querySelector('[data-dr-add-text]');

      this.bindOptions();
      this.bindGallery();
      this.bindQuantity();
    }

    readVariants() {
      const node = this.root.querySelector('[data-dr-variants]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent) || [];
      } catch (e) {
        return [];
      }
    }

    optionFieldsets() {
      return Array.from(this.root.querySelectorAll('[data-dr-option-index]'));
    }

    selectedOptions() {
      return this.optionFieldsets().map((field) => {
        const select = field.querySelector('[data-dr-option-select]');
        if (select) return select.value;
        const checked = field.querySelector('[data-dr-option-input]:checked');
        return checked ? checked.value : null;
      });
    }

    bindOptions() {
      this.optionFieldsets().forEach((field) => {
        field.addEventListener('change', () => this.onOptionChange());
      });
    }

    onOptionChange() {
      const chosen = this.selectedOptions();

      // Reflect the chosen value in each fieldset's legend.
      this.optionFieldsets().forEach((field, i) => {
        const label = field.querySelector('[data-dr-option-selected]');
        if (label && chosen[i]) label.textContent = chosen[i];
      });

      const variant = this.variants.find((v) =>
        chosen.every((value, i) => value === null || v.options[i] === value)
      );

      this.apply(variant);
    }

    apply(variant) {
      if (!variant) {
        this.setUnavailable();
        return;
      }

      if (this.variantInput) {
        this.variantInput.value = variant.id;
        this.variantInput.disabled = !variant.available;
      }

      setPrice(this.priceCurrent, money(variant.price, this.moneyFormat));

      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (this.priceWas) {
        this.priceWas.hidden = !onSale;
        if (onSale) setPrice(this.priceWas, money(variant.compare_at_price, this.moneyFormat));
      }
      if (this.priceSave) {
        this.priceSave.hidden = !onSale;
        if (onSale) {
          const off = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          this.priceSave.textContent = 'Save ' + off + '%';
        }
      }

      if (this.addButton) {
        this.addButton.disabled = !variant.available;
        if (this.addText) {
          this.addText.textContent = variant.available ? 'Add to cart' : 'Sold out';
        }
      }

      this.showMedia(variant.featured_media && variant.featured_media.id);

      const url = this.root.dataset.url;
      if (url && window.history && window.history.replaceState) {
        window.history.replaceState({}, '', url + '?variant=' + variant.id);
      }
    }

    setUnavailable() {
      if (this.variantInput) this.variantInput.disabled = true;
      if (this.addButton) this.addButton.disabled = true;
      if (this.addText) this.addText.textContent = 'Unavailable';
    }

    // ------------------------------------------------------------ gallery
    bindGallery() {
      this.slides = Array.from(this.root.querySelectorAll('[data-dr-gallery-slide]'));
      this.thumbs = Array.from(this.root.querySelectorAll('[data-dr-gallery-thumb]'));

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.goToSlide(parseInt(thumb.dataset.drGalleryThumb, 10)));
      });
    }

    goToSlide(index) {
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      this.thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', String(active));
      });
    }

    showMedia(mediaId) {
      if (!mediaId || !this.slides) return;
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.goToSlide(index);
    }

    // ----------------------------------------------------------- quantity
    bindQuantity() {
      const input = this.root.querySelector('.dr-qty__input');
      if (!input) return;

      this.root.querySelectorAll('[data-dr-qty]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = parseInt(btn.dataset.drQty, 10);
          const next = Math.max(1, (parseInt(input.value, 10) || 1) + step);
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }

  function init() {
    document.querySelectorAll('[data-dr-product]').forEach((root) => {
      if (root.dataset.drInit) return;
      root.dataset.drInit = 'true';
      new DrProduct(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
